import { z } from "zod";
import { createEndpoint, createEndpointWithPagination } from "../createEndpoint";
import type { HttpClient, QueryParams } from "../httpClient";
import {
    AdminProjectDetailResponseSchema,
    AdminProjectListItemSchema,
    AdminProjectsV2ListQueryParams,
    AdminStoreProjectParticipantRequest,
    AdminUpdateProjectQuotePaymentStatusRequest,
    AdminUpdateProjectQuoteStatusRequest,
    AdminUpdateProjectStatusRequest,
} from "../schemas/projects-v2";

export function createAdminProjectsV2Api(http: HttpClient, baseUrl: string) {
    const adminProjectsBaseUrl = `${baseUrl}/admin/projects`;

    return {
        listProjects: createEndpointWithPagination<
            z.infer<typeof AdminProjectListItemSchema>,
            unknown,
            unknown,
            AdminProjectsV2ListQueryParams
        >(http, "GET", "", {
            base: adminProjectsBaseUrl,
            itemSchema: AdminProjectListItemSchema,
        }),

        getProject: createEndpoint<
            z.infer<typeof AdminProjectDetailResponseSchema>,
            unknown,
            QueryParams,
            { projectId: number }
        >(http, "GET", "/:projectId", {
            base: adminProjectsBaseUrl,
            responseSchema: AdminProjectDetailResponseSchema,
            params: ["projectId"],
        }),

        updateStatus: createEndpoint<
            z.infer<typeof AdminProjectDetailResponseSchema>,
            AdminUpdateProjectStatusRequest,
            QueryParams,
            { projectId: number }
        >(http, "POST", "/:projectId/status", {
            base: adminProjectsBaseUrl,
            responseSchema: AdminProjectDetailResponseSchema,
            params: ["projectId"],
        }),

        updateQuoteStatus: createEndpoint<
            z.infer<typeof AdminProjectDetailResponseSchema>,
            AdminUpdateProjectQuoteStatusRequest,
            QueryParams,
            { projectId: number; quoteId: number }
        >(http, "POST", "/:projectId/quotes/:quoteId/status", {
            base: adminProjectsBaseUrl,
            responseSchema: AdminProjectDetailResponseSchema,
            params: ["projectId", "quoteId"],
        }),

        updateQuotePaymentStatus: createEndpoint<
            z.infer<typeof AdminProjectDetailResponseSchema>,
            AdminUpdateProjectQuotePaymentStatusRequest,
            QueryParams,
            { projectId: number; quoteId: number }
        >(http, "POST", "/:projectId/quotes/:quoteId/payment-status", {
            base: adminProjectsBaseUrl,
            responseSchema: AdminProjectDetailResponseSchema,
            params: ["projectId", "quoteId"],
        }),

        addParticipant: createEndpoint<
            z.infer<typeof AdminProjectDetailResponseSchema>,
            AdminStoreProjectParticipantRequest,
            QueryParams,
            { projectId: number }
        >(http, "POST", "/:projectId/participants", {
            base: adminProjectsBaseUrl,
            responseSchema: AdminProjectDetailResponseSchema,
            params: ["projectId"],
        }),

        removeParticipant: createEndpoint<
            z.infer<typeof AdminProjectDetailResponseSchema>,
            unknown,
            QueryParams,
            { projectId: number; participantId: number }
        >(http, "DELETE", "/:projectId/participants/:participantId", {
            base: adminProjectsBaseUrl,
            responseSchema: AdminProjectDetailResponseSchema,
            params: ["projectId", "participantId"],
        }),

        unmatch: createEndpoint<
            z.infer<typeof AdminProjectDetailResponseSchema>,
            unknown,
            QueryParams,
            { projectId: number }
        >(http, "POST", "/:projectId/unmatch", {
            base: adminProjectsBaseUrl,
            responseSchema: AdminProjectDetailResponseSchema,
            params: ["projectId"],
        }),
    };
}

export type AdminProjectsV2Api = ReturnType<typeof createAdminProjectsV2Api>;
